// -- [Attribution: initial URL, referrer, UTM params, and optional client IP for demo lead tracking]

const INITIAL_URL_KEY = 'drivible_attribution_initial_url'
const IP_CACHE_KEY = 'drivible_attribution_ip'
const IP_CACHE_MS = 60_000

// -- [Capture landing URL on first load so we can send it with the demo form even after SPA navigation]
if (typeof window !== 'undefined') {
  try {
    if (!sessionStorage.getItem(INITIAL_URL_KEY)) {
      sessionStorage.setItem(INITIAL_URL_KEY, window.location.href)
    }
  } catch {
    // -- [sessionStorage may be unavailable]
  }
}

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_keyword',
] as const

/** -- UTM param name to attribution object key (e.g. utm_source → "UTM-Source") */
function utmParamToKey(param: string): string {
  const part = param.replace(/^utm_/, '')
  return 'UTM-' + (part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
}

/**
 * Parses UTM and related params from a URL search string.
 * Supports utm_source, utm_medium, utm_campaign, utm_content, utm_term, utm_keyword.
 */
function parseUtmParams(search: string): Record<string, string> {
  const out: Record<string, string> = {}
  const params = new URLSearchParams(search)
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value != null && value.trim() !== '') {
      out[utmParamToKey(key)] = value.trim()
    }
  }
  return out
}

/** -- Synchronous attribution data (referrer, initial URL, UTM from initial URL) */
export function getAttributionSync(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {}
  }
  const attribution: Record<string, string> = {}
  const referrer = document.referrer
  if (referrer) attribution.referrer = referrer

  try {
    const initialUrl = sessionStorage.getItem(INITIAL_URL_KEY) ?? window.location.href
    attribution.initialUrl = initialUrl
    const idx = initialUrl.indexOf('?')
    const search = idx >= 0 ? initialUrl.slice(idx) : ''
    const utm = parseUtmParams(search)
    Object.assign(attribution, utm)
  } catch {
    attribution.initialUrl = window.location.href
  }
  return attribution
}

let ipPromise: Promise<string | null> | null = null

function setIpCache(ip: string): void {
  try {
    sessionStorage.setItem(IP_CACHE_KEY, ip)
    sessionStorage.setItem(IP_CACHE_KEY + '_at', String(Date.now()))
  } catch {
    // ignore
  }
}

/** -- Fetches client IP (cached for a short time). Tries AWS checkip first, then ipify. */
function fetchClientIp(): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (ipPromise) return ipPromise
  ipPromise = (async () => {
    try {
      const cached = sessionStorage.getItem(IP_CACHE_KEY)
      const cachedAt = sessionStorage.getItem(IP_CACHE_KEY + '_at')
      if (cached && cachedAt) {
        const age = Date.now() - parseInt(cachedAt, 10)
        if (age < IP_CACHE_MS) return cached
      }

      // -- [Primary: AWS checkip returns plain text IP]
      let ip: string | null = null
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 4000)
        const res = await fetch('https://checkip.amazonaws.com/', { signal: controller.signal })
        clearTimeout(timeout)
        if (res.ok) {
          const text = (await res.text()).trim()
          if (text) ip = text
        }
      } catch {
        // fall through to ipify
      }
      if (ip) {
        setIpCache(ip)
        return ip
      }

      // -- [Fallback: ipify JSON]
      const controller2 = new AbortController()
      const timeout2 = setTimeout(() => controller2.abort(), 4000)
      const res = await fetch('https://api.ipify.org?format=json', { signal: controller2.signal })
      clearTimeout(timeout2)
      if (!res.ok) return null
      const data = (await res.json()) as { ip?: string }
      ip = typeof data.ip === 'string' ? data.ip.trim() : null
      if (ip) setIpCache(ip)
      return ip
    } catch {
      return null
    }
  })()
  return ipPromise
}

/**
 * Full attribution object for the demo lead payload: referrer, initialUrl, UTM-* params, and ipAddress (if available).
 * Call this when building the submission payload; IP is fetched with a short timeout and may be missing.
 */
export async function getAttribution(timeoutMs = 3000): Promise<Record<string, string>> {
  const attribution = { ...getAttributionSync() }
  try {
    const ip = await Promise.race([
      fetchClientIp(),
      new Promise<string | null>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), timeoutMs)
      ),
    ])
    if (ip) attribution.ipAddress = ip
  } catch {
    // -- [IP optional; leave out if fetch fails or times out]
  }
  return attribution
}
